import React, { useState } from 'react';
import { Search, Bell, User, Home, ChevronDown, Building, Folder, ArrowRight, LayoutDashboard, Shield, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

const Header = ({ 
  currentBoard, 
  currentWorkspace, 
  workspaces, 
  allBoards, 
  onWorkspaceChange, 
  onBoardSelect,
  onGoHome,
  user,
  onLogout,
  onAdminPanel,
  onSettings
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Get all due cards and lists across all boards
  const getDueItems = () => {
    const dueItems = [];
    
    allBoards?.forEach(board => {
      const workspace = workspaces?.find(w => w.id === board.workspace_id);
      
      board.lists?.forEach(list => {
        // Check if list has due date
        if (list.due_date) {
          const now = new Date();
          const due = new Date(list.due_date);
          const timeDiff = due.getTime() - now.getTime();
          const daysDiff = timeDiff / (1000 * 3600 * 24);
          
          // Include overdue and due within 24 hours
          if (daysDiff <= 1) {
            const status = daysDiff < 0 ? 'overdue' : 'due-soon';
            dueItems.push({
              id: list.id,
              title: list.title,
              due_date: list.due_date,
              type: 'list',
              boardTitle: board.title,
              boardColor: board.color,
              listTitle: list.title,
              workspaceName: workspace?.name || 'Unknown Workspace',
              workspaceId: workspace?.id,
              status,
              daysRemaining: Math.ceil(daysDiff)
            });
          }
        }
        
        // Check cards within this list
        list.cards?.forEach(card => {
          if (card.due_date) {
            const now = new Date();
            const due = new Date(card.due_date);
            const timeDiff = due.getTime() - now.getTime();
            const daysDiff = timeDiff / (1000 * 3600 * 24);
            
            // Include overdue and due within 24 hours
            if (daysDiff <= 1) {
              const status = daysDiff < 0 ? 'overdue' : 'due-soon';
              dueItems.push({
                ...card,
                type: 'card',
                boardTitle: board.title,
                boardColor: board.color,
                listTitle: list.title,
                workspaceName: workspace?.name || 'Unknown Workspace',
                workspaceId: workspace?.id,
                status,
                daysRemaining: Math.ceil(daysDiff)
              });
            }
          }
        });
      });
    });
    
    return dueItems;
  };

  const dueItems = getDueItems();
  const hasDueItems = dueItems.length > 0;
  
  // Separate overdue and due-soon items (cards and lists)
  const overdueItems = dueItems.filter(item => item.status === 'overdue');
  const dueSoonItems = dueItems.filter(item => item.status === 'due-soon');
  const hasOverdueItems = overdueItems.length > 0;
  const hasDueSoonItems = dueSoonItems.length > 0;

  // Group due items by workspace
  const dueItemsByWorkspace = dueItems.reduce((acc, item) => {
    const workspaceId = item.workspaceId || 'unknown';
    if (!acc[workspaceId]) {
      acc[workspaceId] = {
        workspaceName: item.workspaceName,
        items: []
      };
    }
    acc[workspaceId].items.push(item);
    return acc;
  }, {});

  const handleItemClick = (item) => {
    // Find the board and navigate to it
    const board = allBoards?.find(b => b.title === item.boardTitle);
    if (board) {
      onBoardSelect(board);
      setShowNotifications(false);
    }
  };

  const formatDueText = (card) => {
    if (card.status === 'overdue') {
      const daysOverdue = Math.abs(card.daysRemaining);
      return daysOverdue === 0 ? 'Due today' : `${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue`;
    } else {
      return card.daysRemaining === 0 ? 'Due today' : `Due in ${card.daysRemaining} day${card.daysRemaining > 1 ? 's' : ''}`;
    }
  };

  // Search functionality
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase().trim();
    const results = [];

    // Search workspaces
    workspaces?.forEach(workspace => {
      if (workspace.name.toLowerCase().includes(query) || 
          workspace.description?.toLowerCase().includes(query)) {
        results.push({
          type: 'workspace',
          id: workspace.id,
          title: workspace.name,
          description: workspace.description,
          workspace: workspace
        });
      }
    });

    // Search boards
    allBoards?.forEach(board => {
      const workspace = workspaces?.find(w => w.id === board.workspace_id);
      if (board.title.toLowerCase().includes(query)) {
        results.push({
          type: 'board',
          id: board.id,
          title: board.title,
          description: `Board in ${workspace?.name || 'Unknown Workspace'}`,
          color: board.color,
          board: board
        });
      }
    });

    // Search cards
    allBoards?.forEach(board => {
      const workspace = workspaces?.find(w => w.id === board.workspace_id);
      board.lists?.forEach(list => {
        list.cards?.forEach(card => {
          if (card.title.toLowerCase().includes(query) || 
              card.description?.toLowerCase().includes(query)) {
            results.push({
              type: 'card',
              id: card.id,
              title: card.title,
              description: `Card in ${list.title} • ${board.title}`,
              workspaceName: workspace?.name || 'Unknown Workspace',
              board: board,
              card: card
            });
          }
        });
      });
    });

    return results.slice(0, 10); // Limit to 10 results
  };

  const searchResults = getSearchResults();
  const hasSearchResults = searchQuery.trim() && searchResults.length > 0;

  const handleSearchResultClick = (result) => {
    if (result.type === 'workspace') {
      onWorkspaceChange(result.workspace);
    } else if (result.type === 'board') {
      onBoardSelect(result.board);
    } else if (result.type === 'card') {
      onBoardSelect(result.board);
    }
    setShowSearchResults(false);
    setSearchQuery('');
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(value.trim().length > 0);
  };

  return (
    <>
    <header className="bg-[#4FC3F7] text-white px-4 py-2 flex items-center justify-between shadow-sm">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          {/* Clean header - logo removed as requested */}
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
            onClick={onGoHome}
          >
            <Home className="h-4 w-4 mr-1" />
            Home
          </Button>
        </div>
        
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <LayoutDashboard className="h-4 w-4 mr-1" />
                Boards
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 max-h-96 overflow-y-auto">
              <DropdownMenuLabel>All Workspaces & Boards</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {workspaces && workspaces.length > 0 ? (
                workspaces.map((workspace) => {
                  const workspaceBoards = allBoards?.filter(board => board.workspace_id === workspace.id) || [];
                  
                  return (
                    <div key={workspace.id}>
                      <DropdownMenuItem
                        onClick={() => onWorkspaceChange(workspace)}
                        className={`font-medium ${
                          currentWorkspace?.id === workspace.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <Building className="h-4 w-4 mr-2" />
                        {workspace.name}
                        {workspace.description && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({workspaceBoards.length} boards)
                          </span>
                        )}
                      </DropdownMenuItem>
                      
                      {workspaceBoards.length > 0 && (
                        <div className="ml-6 border-l-2 border-gray-100">
                          {workspaceBoards.slice(0, 5).map((board) => (
                            <DropdownMenuItem
                              key={board.id}
                              onClick={() => onBoardSelect(board)}
                              className="pl-4"
                            >
                              <div 
                                className="w-3 h-3 rounded mr-2 flex-shrink-0"
                                style={{ backgroundColor: board.color }}
                              ></div>
                              <span className="truncate">{board.title}</span>
                              {board.starred && (
                                <span className="ml-auto text-yellow-500">★</span>
                              )}
                            </DropdownMenuItem>
                          ))}
                          {workspaceBoards.length > 5 && (
                            <DropdownMenuItem
                              onClick={() => onWorkspaceChange(workspace)}
                              className="pl-4 text-gray-500 text-sm"
                            >
                              <Folder className="h-3 w-3 mr-2" />
                              View all {workspaceBoards.length} boards
                            </DropdownMenuItem>
                          )}
                        </div>
                      )}
                      
                      {workspace.id !== workspaces[workspaces.length - 1].id && (
                        <DropdownMenuSeparator />
                      )}
                    </div>
                  );
                })
              ) : (
                <DropdownMenuItem disabled>
                  No workspaces found
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Center Section - Search */}
      {/* Desktop Search */}
      <div className="hidden md:flex flex-1 max-w-md mx-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search boards, cards, and workspaces..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            className="w-full pl-10 bg-white/30 border-white/40 text-white placeholder-white/80 focus:bg-white focus:text-gray-900 focus:placeholder-gray-500"
          />
          
          {/* Search Results Dropdown */}
          {showSearchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border max-h-96 overflow-y-auto z-50">
              <div className="px-3 py-2 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">Search Results</span>
                  {searchQuery.trim() && (
                    <span className="text-xs text-gray-500">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="max-h-80 overflow-y-auto">
                {!hasSearchResults && searchQuery.trim() ? (
                  <div className="px-3 py-6 text-center">
                    <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <span className="text-gray-500 text-sm">No results found for "{searchQuery}"</span>
                  </div>
                ) : hasSearchResults ? (
                  searchResults.map((result) => (
                    <div
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSearchResultClick(result)}
                      className="px-3 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-start space-x-3 w-full">
                        <div className="flex-shrink-0 mt-1">
                          {result.type === 'workspace' && <Building className="h-4 w-4 text-blue-600" />}
                          {result.type === 'board' && (
                            <div 
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: result.color }}
                            ></div>
                          )}
                          {result.type === 'card' && <div className="w-4 h-4 rounded bg-gray-300"></div>}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium truncate text-gray-900">
                              {result.title}
                            </span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full uppercase">
                              {result.type}
                            </span>
                          </div>
                          
                          <div className="text-xs text-gray-500 truncate">
                            {result.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-gray-500 text-sm">
                    Start typing to search...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Search Button */}
      <div className="md:hidden">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowMobileSearch(true)}
          className="text-white hover:bg-white/20 p-2"
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-3">
        <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`relative p-2 ${
                hasOverdueItems 
                  ? 'blink-red hover:bg-red-700' 
                  : hasDueSoonItems
                  ? 'blink-yellow hover:bg-yellow-700'
                  : 'text-white hover:bg-white/20'
              }`}
            >
              <Bell className="h-4 w-4" />
              {hasDueItems && (
                <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full flex items-center justify-center ${
                  hasOverdueItems ? 'bg-red-500' : 'bg-yellow-500'
                }`}>
                  <span className="text-xs text-white font-bold">{dueItems.length}</span>
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-y-auto">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Due Items</span>
              {hasDueItems && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                  {dueItems.length} item{dueItems.length > 1 ? 's' : ''}
                </span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {!hasDueItems ? (
              <DropdownMenuItem disabled className="text-center py-4">
                <div className="flex flex-col items-center space-y-2">
                  <Bell className="h-8 w-8 text-gray-300" />
                  <span className="text-gray-500">No due items</span>
                </div>
              </DropdownMenuItem>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {Object.entries(dueItemsByWorkspace).map(([workspaceId, workspace]) => (
                  <div key={workspaceId}>
                    <div className="px-3 py-2 bg-gray-50 border-b">
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4 text-gray-600" />
                        <span className="font-medium text-sm">{workspace.workspaceName}</span>
                        <span className="text-xs text-gray-500">
                          ({workspace.items.length} due)
                        </span>
                      </div>
                    </div>
                    
                    {workspace.items.map((item, index) => (
                      <DropdownMenuItem
                        key={`${item.id}-${index}`}
                        onClick={() => handleItemClick(item)}
                        className="px-3 py-3 cursor-pointer hover:bg-gray-50"
                      >
                        <div className="flex items-start space-x-3 w-full">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            item.status === 'overdue' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}></div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <div 
                                className="w-3 h-3 rounded flex-shrink-0"
                                style={{ backgroundColor: item.boardColor }}
                              ></div>
                              <span className="text-sm font-medium truncate">
                                {item.title} {item.type === 'list' && '📋'}
                              </span>
                            </div>
                            
                            <div className="text-xs text-gray-500 mb-1">
                              {item.boardTitle}{item.type === 'card' && ` • ${item.listTitle}`}
                            </div>
                            
                            <div className={`text-xs font-medium ${
                              item.status === 'overdue' 
                                ? 'text-red-600' 
                                : 'text-yellow-600'
                            }`}>
                              {formatDueText(item)}
                            </div>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                    
                    {workspaceId !== Object.keys(dueItemsByWorkspace)[Object.keys(dueItemsByWorkspace).length - 1] && (
                      <DropdownMenuSeparator />
                    )}
                  </div>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Admin Panel Button - Only visible to admins */}
        {user?.is_admin && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAdminPanel}
            className="text-white hover:bg-white/20 flex items-center space-x-1"
          >
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Admin</span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 flex items-center space-x-1"
            >
              <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
                <User className="h-4 w-4" />
              </div>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2 border-b">
              <p className="font-medium">{user?.name || 'User'}</p>
              <p className="text-sm text-gray-500">{user?.email || ''}</p>
              {user?.is_admin && (
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-1">
                  Admin
                </span>
              )}
            </div>
            <DropdownMenuItem 
              onClick={onSettings}
              className="cursor-pointer"
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={onLogout}
              className="text-red-600 hover:text-red-700 cursor-pointer"
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>

    {/* Mobile Search Overlay */}
    {showMobileSearch && (
      <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50">
        <div className="bg-white h-full">
          {/* Mobile Search Header */}
          <div className="flex items-center p-4 border-b bg-[#4FC3F7]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowMobileSearch(false);
                setSearchQuery('');
                setShowSearchResults(false);
              }}
              className="text-white hover:bg-white/20 p-2 mr-3"
            >
              <ArrowRight className="h-5 w-5 transform rotate-180" />
            </Button>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search boards, cards, and workspaces..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(e.target.value.trim().length > 0);
                }}
                autoFocus
                className="w-full pl-10 bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>

          {/* Mobile Search Results */}
          <div className="flex-1 overflow-y-auto">
            {searchQuery.trim() ? (
              <div>
                <div className="px-4 py-3 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Search Results</span>
                    <span className="text-xs text-gray-500">
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                
                <div className="pb-4">
                  {!hasSearchResults ? (
                    <div className="px-4 py-8 text-center">
                      <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <span className="text-gray-500">No results found for "{searchQuery}"</span>
                    </div>
                  ) : (
                    searchResults.map((result) => (
                      <div
                        key={`mobile-${result.type}-${result.id}`}
                        onClick={() => {
                          handleSearchResultClick(result);
                          setShowMobileSearch(false);
                        }}
                        className="px-4 py-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start space-x-3 w-full">
                          <div className="flex-shrink-0 mt-1">
                            {result.type === 'workspace' && <Building className="h-5 w-5 text-blue-600" />}
                            {result.type === 'board' && (
                              <div 
                                className="w-5 h-5 rounded"
                                style={{ backgroundColor: result.color }}
                              ></div>
                            )}
                            {result.type === 'card' && <div className="w-5 h-5 rounded bg-gray-300"></div>}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-base font-medium truncate text-gray-900">
                                {result.title}
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full uppercase">
                                {result.type}
                              </span>
                            </div>
                            
                            <div className="text-sm text-gray-500 truncate">
                              {result.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg font-medium mb-2">Search Everything</p>
                <p className="text-sm">Find boards, cards, and workspaces quickly</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;